import React from 'react';
import PropTypes from 'prop-types';
import {
  FDAccordion,
  FDAccordionDetails,
  FDAccordionTitle,
  FDStack,
  FDTitleText,
} from '@facet-wealth/design';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import LeverFieldLayout from './LeverLayouts/leverFieldLayout';
import LeverSubAccordionLayout from './leverSubAccordionLayout';
import { metaOverrideSet } from './metaOverrideSet';


const useStyles = buildUseStyles('LeversSidebarLayout', (theme) => ({
  pinnedLeversContainer: {
    '&& .FD-MuiButtonBase-root.FD-MuiAccordionSummary-root': { // Main accordion summary
      padding: 0,
      paddingTop: theme.spacing(3),
      justifyContent: 'flex-start', // Align labels at start of accordion
    },
    '&&& .FD-MuiAccordionSummary-content': { // Accordion summary content
      margin: 0,
      // Make accordion titles left aligned instead of space-between'd
      flex: 'none',
      justifyContent: 'normal',
      alignItems: 'normal',
    },
    '&&& .FD-MuiAccordionSummary-expandIconWrapper': { // svg wrapper
      paddingLeft: 0,
      '& svg': { // chevron svg
        fontSize: theme.spacing(4),
      },
    },
    '&&&&&&& .FD-MuiAccordionDetails-root': { // Accordion details
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  leverGroupTitle: {
    textDecoration: `underline 2px solid ${theme.palette.facet.oceana500}`,
    cursor: 'pointer',
    '& > .FD-MuiTypography-root': {
      color: theme.palette.facet.oceana500,
    },
    padding: theme.spacing(1, 0),
  },
}));

const LeverAccordionLayout = ({
  expandedIDs, setExpandedIDs, loading, setLoading, setSaving, pinnedByGroup, pinnedCount, groupList, searchTerm, onTogglePinOptimistic, onLeverUpdate,
}) => {
  const classes = useStyles();

  const onClickPinnedGroupTitle = (grp) => {
    return (e) => {
      e.preventDefault();
      // Expand every ancestor group (and the target group) so the content is visible
      let idsToExpand = [];
      const hasAncestors = Array.isArray(grp.ancestorIdsToExpand) && grp.ancestorIdsToExpand.length > 0;
      if (hasAncestors) {
        idsToExpand = grp.ancestorIdsToExpand;
      } else if (grp.id) {
        idsToExpand = [grp.id];
      }

      if (idsToExpand.length && setExpandedIDs) {
        setExpandedIDs(() => {
          const next = new Set(['__pinned__']); // only expand pinned groups and the target groups
          idsToExpand.forEach(id => next.add(id));
          return next;
        });
      }

      // Scroll to the target (deepest) group
      const targetId = idsToExpand[idsToExpand.length - 1] || grp.id;
      const el = targetId ? document.getElementById(`lever-group-${targetId}`) : null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
    };
  };

  return (
    <>
      {pinnedCount > 0 && (
      <div className={classes.pinnedLeversContainer}>
        <FDAccordion
          expansionID="__pinned__"
          expanded={expandedIDs?.has?.('__pinned__')}
          onChange={(expanded) => {
            const id = '__pinned__';
            setExpandedIDs?.((prev) => {
              const next = new Set(prev ?? []);
              if (expanded) {
                next.add(id);
              } else {
                next.delete(id);
              }
              return next;
            });
          }}
        >
          <FDAccordionTitle>
            <FDTitleText value={`Pinned or active (${pinnedCount})`} color={'textSecondary'} size={'lg'} />
          </FDAccordionTitle>
          <FDAccordionDetails>
            <FDStack direction="column">
              {pinnedByGroup.map((grp) => {
                return (
                  <>
                    <a
                      href={'#'}
                      onClick={onClickPinnedGroupTitle(grp)}
                      className={classes.leverGroupTitle}
                    >
                      <FDTitleText value={grp.label} color={'textSecondary'} />
                    </a>
                    <FDStack direction="column" spacing={1}>
                      {grp.levers.map((leverRes) => (
                        <MetaResourceRenderer
                          key={`pinned-${leverRes.id()}`}
                          resource={leverRes}
                          layout={LeverFieldLayout}
                          metaOverrideSet={metaOverrideSet}
                          LayoutProps={{
                            loading,
                            setLoading,
                            setSaving,
                            onTogglePinOptimistic,
                            onLeverUpdate,
                            isPinnedOptimistically: true,
                          }}
                        />
                      ))}
                    </FDStack>
                  </>
                );
              })}
            </FDStack>
          </FDAccordionDetails>
        </FDAccordion>
      </div>
      )}
      {groupList.map((groupRes) => (
        <MetaResourceRenderer
          key={groupRes.id()}
          resource={groupRes}
          layout={LeverSubAccordionLayout}
          metaOverrideSet={metaOverrideSet}
          LayoutProps={{
            expandedIDs, setExpandedIDs, loading, setLoading, setSaving, searchTerm, onTogglePinOptimistic, onLeverUpdate,
          }}
        />
      ))}
    </>
  );
};

LeverAccordionLayout.propTypes = {
  expandedIDs: PropTypes.instanceOf(Set),
  setExpandedIDs: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setSaving: PropTypes.func,
  pinnedByGroup: PropTypes.array,
  pinnedCount: PropTypes.number,
  groupList: PropTypes.array,
  searchTerm: PropTypes.string,
  onTogglePinOptimistic: PropTypes.func,
  onLeverUpdate: PropTypes.func,
};

export default LeverAccordionLayout;
